import { ApiError, ForbiddenError, UnauthenticatedError } from '@lib/api';
import { cn } from '@lib/cn';

import { Button } from './primitives';

import type { ReactNode } from 'react';

/**
 * The three states every screen backed by the API has, and which the mock had
 * none of.
 *
 * They live here rather than inline per screen so that "the API is down" looks
 * the same everywhere. An operator who learns the shape of a failure on one
 * screen should recognise it on the next, rather than having to work out whether
 * a differently-worded panel means something different.
 */

export function LoadingRows({ label = 'Loading…' }: { label?: string }) {
    return (
        <div className="flex items-center justify-center gap-3 px-5 py-16" aria-busy="true">
            <span
                role="status"
                className="size-4 animate-spin rounded-full border-2 border-gray-200 border-t-brand"
            >
                <span className="sr-only">{label}</span>
            </span>
            <span className="text-[13px] text-gray-500">{label}</span>
        </div>
    );
}

export function EmptyState({
    title,
    description,
    action,
}: {
    title: string;
    description?: ReactNode;
    action?: ReactNode;
}) {
    return (
        <div className="px-5 py-16 text-center">
            <p className="text-[14px] font-semibold text-gray-800">{title}</p>
            {description && (
                <p className="mx-auto mt-1.5 max-w-md text-[13px] leading-relaxed text-gray-500">
                    {description}
                </p>
            )}
            {action && <div className="mt-5 flex justify-center">{action}</div>}
        </div>
    );
}

/**
 * A failed read.
 *
 * Distinguishes a 4xx from everything else on purpose. "Retry" on a 404 is a
 * button that cannot work, and offering it sends an operator round a loop
 * instead of telling them the thing they asked for does not exist.
 */
export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
    const isClientError = error instanceof ApiError && error.status < 500;
    const retryable = onRetry !== undefined && !isClientError;

    return (
        <div className="px-5 py-16 text-center">
            <p className="text-[14px] font-semibold text-gray-800">
                {isClientError ? error.message : "Couldn't load this from the API"}
            </p>
            <p className="mx-auto mt-1.5 max-w-md text-[13px] leading-relaxed text-gray-500">
                {isClientError
                    ? 'Nothing to retry — this is the answer the API gave.'
                    : error.message}
            </p>
            {retryable && (
                <div className="mt-5 flex justify-center">
                    <Button variant="ghost" onClick={onRetry}>
                        Try again
                    </Button>
                </div>
            )}
        </div>
    );
}

/**
 * A failed write, rendered next to the control that failed.
 *
 * A version conflict gets its own wording because it is the one failure that is
 * nobody's mistake: the operator did everything right and a colleague got there
 * first. Telling them to "try again" without saying why would read as a flaky
 * app rather than as the safety check it is.
 *
 * A refusal (403) gets its own for the opposite reason: it is not a failure at
 * all, it is the app working, and it is the one outcome where retrying is
 * guaranteed not to help.
 */
export function WriteError({ error }: { error: Error }) {
    if (error instanceof UnauthenticatedError) return null;

    const conflict = error instanceof ApiError && error.isVersionConflict;
    const refusal = error instanceof ForbiddenError ? error : undefined;
    const refused = refusal !== undefined;
    const issues = error instanceof ApiError ? error.issues : [];

    return (
        <div
            role="alert"
            className={cn(
                'rounded-lg border px-3.5 py-2.5 text-[12.5px] leading-relaxed',
                // A refusal is not a failure. Rendering "that change did not
                // save" in alarming red for something the app was never going
                // to let this operator do reads as a bug, and sends them
                // retrying instead of asking for the grant they need.
                refused
                    ? 'border-amber-200 bg-amber-50 text-amber-900'
                    : 'border-red-200 bg-red-50 text-red-800',
            )}
        >
            <p className="font-semibold">
                {refused
                    ? 'You do not have permission to do that'
                    : conflict
                      ? 'Someone else changed this first'
                      : 'That change did not save'}
            </p>
            <p className={cn('mt-0.5', refused ? 'text-amber-800' : 'text-red-700')}>
                {conflict
                    ? 'The screen has been refreshed with what is actually stored. Check it still needs your change, then redo it.'
                    : error.message}
            </p>
            {refusal && (
                <p className="mt-1 text-amber-800">
                    {refusal.isRouteMisconfigured
                        ? 'This endpoint has not declared who may call it — that is a server-side gap, not something you can grant yourself. Tell the platform team.'
                        : 'Nothing was changed. Ask the platform team to grant you the tier this action needs.'}
                </p>
            )}
            {issues.length > 0 && (
                <ul className="mt-1.5 list-inside list-disc text-red-700">
                    {issues.map((issue) => (
                        <li key={issue.path}>
                            <span className="font-mono">{issue.path}</span> — {issue.message}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/**
 * The ~30-second propagation delay, stated where a write happens.
 *
 * Backend services cache resolved flag state for about 30s, so a change is not
 * live the instant this app confirms it. Without this line an operator flips a
 * flag, checks the product, sees no change, and flips it back — which is a
 * second unnecessary write to production and a genuinely confusing audit trail.
 */
export function PropagationNotice({ className }: { className?: string }) {
    return (
        <p className={className ?? 'text-[12px] leading-relaxed text-gray-500'}>
            Saved changes take up to <strong className="font-semibold">30 seconds</strong> to reach
            running services — they cache resolved flag state. If the product still looks unchanged,
            wait rather than saving again.
        </p>
    );
}
